import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Timer used to debounce closing the menu so brief pointer travel between items
   * (or transient leave events) does not tear down the menu mid-interaction.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #closeTimeout = null;

  /**
   * Timer used to auto-disconnect the mutation observer for the current activation.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #observerCleanupTimeout = null;

  /**
   * Monotonic token identifying the current activation. Async work (observer/rAF)
   * checks this before writing height so stale callbacks from a previous activation
   * can never overwrite the currently active submenu.
   * @type {number}
   */
  #activationToken = 0;

  /** How long to wait before actually closing the menu after the pointer leaves. */
  #closeDelay = 120;

  /**
   * Cached reference to the owning <header-component>. The whole header (menu bar, the
   * gap row beneath it, and the open mega-menu panel) is the "keep open" region.
   * @type {HTMLElement | null}
   */
  #header = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);

    // Close/keep-open is anchored to the whole header so moving the pointer from a
    // menu item down across the gap into the panel (and between items) never closes
    // the menu. Only leaving the header entirely schedules a close.
    this.#header = this.headerComponent;
    this.#header?.addEventListener('pointerleave', this.#onHeaderPointerLeave);
    this.#header?.addEventListener('pointerenter', this.#cancelPendingClose);
    this.#header?.addEventListener('focusout', this.#onHeaderFocusOut);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#header?.removeEventListener('pointerleave', this.#onHeaderPointerLeave);
    this.#header?.removeEventListener('pointerenter', this.#cancelPendingClose);
    this.#header?.removeEventListener('focusout', this.#onHeaderFocusOut);
    this.#cancelPendingClose();
    this.#cleanupMutationObserver();
  }

  /** Pointer left the entire header (incl. the open panel) — schedule the close. */
  #onHeaderPointerLeave = () => {
    this.#scheduleClose();
  };

  /** Focus left the entire header — schedule the close (keyboard navigation). */
  #onHeaderFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.#header?.contains(next)) return;
    this.#scheduleClose();
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = (event) => {
    const related = event?.relatedTarget;
    if (related instanceof Node && this.headerComponent?.contains(related)) return;
    this.#scheduleClose();
  };

  /**
   * Schedule a deferred close of the given item (defaults to the active item).
   * Re-entering any item cancels this via #cancelPendingClose() in activate().
   * @param {HTMLElement | null} [item]
   */
  #scheduleClose = (item = this.#state.activeItem) => {
    this.#cancelPendingClose();
    this.#closeTimeout = setTimeout(() => {
      this.#closeTimeout = null;
      this.#deactivate(item);
    }, this.#closeDelay);
  };

  /** Cancel any pending deferred close. */
  #cancelPendingClose = () => {
    if (this.#closeTimeout != null) {
      clearTimeout(this.#closeTimeout);
      this.#closeTimeout = null;
    }
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    // (Re)entering any item cancels a pending close so the menu stays open while the
    // pointer moves between items or briefly leaves and returns.
    this.#cancelPendingClose();

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Top-level item with nothing to open (e.g. a link without children). Don't tear an
    // open menu down instantly — defer the close so sweeping across it doesn't flicker.
    if (!submenu) {
      this.#scheduleClose();
      return;
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // Token identifying this activation; async callbacks below bail if superseded.
    const token = ++this.#activationToken;

    // Mark submenu as active for content-visibility optimization
    submenu.dataset.active = '';

    // Cleanup any existing mutation observer/timer from previous menu activations
    this.#cleanupMutationObserver();

    // Monitor DOM mutations to catch deferred content injection (from section hydration)
    this.#submenuMutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
        requestAnimationFrame(() => {
          if (token !== this.#activationToken) return;
          if (submenu.offsetHeight > 0) {
            this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
            this.#setFullOpenHeaderHeight(submenu.offsetHeight);
            this.#cleanupMutationObserver();
          }
        });
      });
    });
    this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

    // Auto-disconnect after 500ms to prevent memory leaks (scoped to this activation)
    this.#observerCleanupTimeout = setTimeout(() => {
      this.#observerCleanupTimeout = null;
      this.#cleanupMutationObserver();
    }, 500);

    let finalHeight = submenu.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');

    // Re-measure once after layout settles, regardless of DOM mutations. For
    // server-rendered submenus a mutation may never fire, and offsetHeight read
    // synchronously right after flipping content-visibility can be stale, leaving the
    // panel collapsed. This guarantees the rested item ends at its true height.
    if (isDefaultSlot) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (token !== this.#activationToken) return;
          const measured = submenu.offsetHeight;
          if (measured > 0 && measured !== finalHeight) {
            this.headerComponent?.style.setProperty('--submenu-height', `${measured}px`);
            this.#setFullOpenHeaderHeight(measured);
          }
        });
      });
    }
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // Keep the menu open while the pointer/focus stays anywhere inside the header — the
    // menu bar, the gap row beneath it, the open mega-menu panel, or the overflow menu.
    // This is what lets you move down into the panel to pick a submenu item without it
    // folding back in. Closing when the interaction truly leaves the header is handled
    // by the header-level pointerleave/focusout listeners.
    const related = event.relatedTarget;
    if (related instanceof Node && this.headerComponent?.contains(related)) return;

    // Schedule a deferred close of the item that was actually left. #deactivate() bails
    // if that item is no longer the active one, so a late/duplicate leave for a
    // superseded item can never tear down the item the pointer is now resting on.
    const leftItem = findMenuItem(event.target);
    this.#scheduleClose(leftItem ?? this.#state.activeItem);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
    if (this.#observerCleanupTimeout != null) {
      clearTimeout(this.#observerCleanupTimeout);
      this.#observerCleanupTimeout = null;
    }
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
